import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, InfoSmallIcon, InputNumber, Label, Tooltip } from "@activecollab/components";
import { Field, Form, Formik } from "formik";
import PropTypes from "prop-types";
import { useFocusInvalidField } from "~/components/Hooks/useFocusInvalidField";
import { TableDialogValidator } from "../formValidators";

export const TableDialog = ({ onClose, onSubmit, ...rest }) => {
  const { t } = useTranslation();
  const { fieldRef, focusFirstInvalidField } = useFocusInvalidField();

  const handleSubmit = useCallback(
    ({ rows, columns }) => {
      onSubmit?.(Number(rows), Number(columns));
      onClose?.();
    },
    [onSubmit, onClose]
  );

  return (
    <Dialog
      {...rest}
      onClose={onClose}
    >
      <Formik
        initialValues={{ columns: 3, rows: 2 }}
        validationSchema={TableDialogValidator}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {({ errors, submitForm }) => (
          <Form role="form">
            <Dialog.Title>{t("Insert Table")}</Dialog.Title>
            <Dialog.ContentDivider />
            <Dialog.Content>
              <div className="tw-flex tw-items-center tw-gap-4">
                <div>
                  <Field name="columns">
                    {({ field, form, meta }) => (
                      <>
                        <Label
                          size="small"
                          htmlFor="columns"
                          className="tw-mb-1"
                          invalid={meta.error}
                        >
                          {t("Columns")}
                        </Label>
                        <InputNumber
                          allowEmptyValue
                          name={field.name}
                          value={field.value}
                          id="columns"
                          invalid={errors.columns}
                          className="tw-w-24"
                          min={1}
                          max={15}
                          decimalLength={0}
                          autoFocus
                          onChange={(val) => {
                            form.setFieldTouched(field.name, true);
                            form.setFieldValue(field.name, val);
                          }}
                          endAdornment={
                            errors.columns ? (
                              <Tooltip title={errors.columns}>
                                <InfoSmallIcon
                                  fill="var(--red-alert)"
                                  className="tw-cursor-help tw-mr-1"
                                />
                              </Tooltip>
                            ) : null
                          }
                          ref={(el) => (fieldRef.current.columns = el)}
                        />
                      </>
                    )}
                  </Field>
                </div>
                <div>
                  <Field name="rows">
                    {({ field, form, meta }) => (
                      <>
                        <Label
                          size="small"
                          htmlFor="rows"
                          className="tw-mb-1"
                          invalid={meta.error}
                        >
                          {t("Rows")}
                        </Label>
                        <InputNumber
                          allowEmptyValue
                          name={field.name}
                          value={field.value}
                          id="rows"
                          invalid={errors.rows}
                          className="tw-w-24"
                          min={1}
                          max={100}
                          decimalLength={0}
                          onChange={(val) => {
                            form.setFieldTouched(field.name, true);
                            form.setFieldValue(field.name, val);
                          }}
                          endAdornment={
                            errors.rows ? (
                              <Tooltip title={errors.rows}>
                                <InfoSmallIcon
                                  fill="var(--red-alert)"
                                  className="tw-cursor-help tw-mr-1"
                                />
                              </Tooltip>
                            ) : null
                          }
                          ref={(el) => (fieldRef.current.rows = el)}
                        />
                      </>
                    )}
                  </Field>
                </div>
              </div>
            </Dialog.Content>
            <Dialog.ContentDivider />
            <Dialog.Actions>
              <Button
                type="submit"
                className="tw-mr-3"
                onClick={() => {
                  focusFirstInvalidField(errors, false);
                  submitForm();
                }}
              >
                {t("Save")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
              >
                {t("Cancel")}
              </Button>
            </Dialog.Actions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

TableDialog.propTypes = {
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};
